import React, { useState, useEffect, useRef } from "react";
import * as d3 from "d3";
import { Node, NetworkStats, VisualNode, VisualEdge } from "./interface";
import { Network } from "./network";

const NetworkVisualizer = () => {
    const maxNodeSize = 15;
    const minNodeSize = 5;

    const [network, setNetwork] = useState<Network | null>(null);
    const [stats, setStats] = useState<NetworkStats | null>(null);
    const [simulationStep, setSimulationStep] = useState(0);
    const [initialNodes, setInitialNodes] = useState(5);
    const [minCapacity, setMinCapacity] = useState(5);
    const [maxCapacity, setMaxCapacity] = useState(20);
    const [edgesPerNode, setEdgesPerNode] = useState(2);
    const [simulating, setSimulating] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [stepDelay, setStepDelay] = useState(250);
    const [isInitialized, setIsInitialized] = useState(false);
    const [highestDegreeNode, setHighestDegreeNode] = useState<Node | null>(
        null
    );
    const [networkHistory, setNetworkHistory] = useState<Network[]>([]);
    const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1);
    const [sheddingEvents, setSheddingEvents] = useState<boolean[]>([]);

    const networkRef = useRef<HTMLDivElement>(null);
    const distributionRef = useRef<HTMLDivElement>(null);
    const scatterRef = useRef<HTMLDivElement>(null);
    const simulationRef = useRef<Network | null>(null);
    const animationRef = useRef<NodeJS.Timeout | null>(null);

    // Initialize simulation
    const initializeSimulation = () => {
        if (isInitialized) {
            // Clear the network
            setNetwork(null);
            setStats(null);
            setSimulationStep(0);
            setIsInitialized(false);
            setIsPlaying(false);
            setHighestDegreeNode(null);
            setNetworkHistory([]);
            setCurrentHistoryIndex(-1);
            setSheddingEvents([]);
            return;
        }

        setSimulating(true);
        setSimulationStep(0);

        // Create new network with initial nodes
        const net = new Network({
            initialNodes,
            minCapacity,
            maxCapacity,
            shedPercent: 0.5,
            m: edgesPerNode,
        });
        simulationRef.current = net;

        // Update state
        setNetwork(net);
        setStats(net.getStatistics());
        updateHighestDegreeNode(net);
        setNetworkHistory([net.clone()]);
        setCurrentHistoryIndex(0);
        setSheddingEvents([false]);
        setSimulating(false);
        setIsInitialized(true);
    };

    // Memoize the step function to avoid recreation on every render
    const stepSimulation = React.useCallback(() => {
        if (!simulationRef.current) {
            setIsPlaying(false);
            return;
        }

        const net = simulationRef.current;

        // Add one node and perform preferential attachment
        const { didShed } = net.simulate();

        // Update step counter
        setSimulationStep((prevStep) => prevStep + 1);

        // Update statistics and visualization
        setNetwork(net);
        setStats(net.getStatistics());
        updateHighestDegreeNode(net);

        // Update history
        setNetworkHistory((prevHistory) => {
            const newHistory = prevHistory.slice(0, currentHistoryIndex + 1);
            newHistory.push(net.clone());
            return newHistory;
        });
        setSheddingEvents((prevEvents) => {
            const newEvents = prevEvents.slice(0, currentHistoryIndex + 1);
            newEvents.push(didShed);
            return newEvents;
        });
        setCurrentHistoryIndex((prevIndex) => prevIndex + 1);
    }, [currentHistoryIndex]);

    // Handle history slider change
    const handleHistoryChange = (index: number) => {
        if (index < 0 || index >= networkHistory.length) return;

        const historicalNetwork = networkHistory[index];
        setNetwork(historicalNetwork);
        setStats(historicalNetwork.getStatistics());
        updateHighestDegreeNode(historicalNetwork);
        setSimulationStep(index);
        setCurrentHistoryIndex(index);
    };

    // Find the node with the highest degree
    const updateHighestDegreeNode = (net: Network) => {
        if (!net || net.nodes.length === 0) return;

        let maxDegree = -1;
        let maxDegreeNode = null;

        net.nodes.forEach((node) => {
            if (node.degree > maxDegree) {
                maxDegree = node.degree;
                maxDegreeNode = { ...node };
            }
        });

        setHighestDegreeNode(maxDegreeNode);
    };

    // Play/pause simulation
    useEffect(() => {
        if (isPlaying) {
            animationRef.current = setInterval(stepSimulation, stepDelay);
        } else if (animationRef.current) {
            clearInterval(animationRef.current);
        }

        return () => {
            if (animationRef.current) {
                clearInterval(animationRef.current);
            }
        };
    }, [isPlaying, stepDelay, stepSimulation]);

    // Draw network visualization
    useEffect(() => {
        if (!network || !networkRef.current) return;

        // Clear previous visualization
        d3.select(networkRef.current).selectAll("*").remove();

        // Get container dimensions
        const container = networkRef.current;
        const width = container.clientWidth;
        const height = container.clientHeight;

        // Create SVG
        const svg = d3
            .select(networkRef.current)
            .append("svg")
            .attr("width", "100%")
            .attr("height", "100%")
            .attr("viewBox", `0 0 ${width} ${height}`)
            .attr("preserveAspectRatio", "xMidYMid meet");

        // Handle resize
        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const newWidth = entry.contentRect.width;
                const newHeight = entry.contentRect.height;
                svg.attr("viewBox", `0 0 ${newWidth} ${newHeight}`);

                // Update force simulation center
                if (simulation) {
                    simulation.force(
                        "center",
                        d3.forceCenter(newWidth / 2, newHeight / 2)
                    );
                    simulation.alpha(0.3).restart();
                }
            }
        });

        resizeObserver.observe(container);

        // Prepare data for visualization
        const nodes: VisualNode[] = network.nodes.map((node) => ({
            id: node.id,
            degree: node.degree,
            capacity: node.capacity,
            atCapacity: node.degree >= node.capacity,
            isHighestDegree: highestDegreeNode
                ? node.id === highestDegreeNode.id
                : false,
            x: 0,
            y: 0,
            vx: 0,
            vy: 0,
            index: node.id,
        }));

        const links: VisualEdge[] = network.edges.map((edge) => ({
            source: nodes[edge.source],
            target: nodes[edge.target],
            isHighDegree: highestDegreeNode
                ? edge.source === highestDegreeNode.id ||
                  edge.target === highestDegreeNode.id
                : false,
        }));

        // Scale node size based on degree
        const getNodeSize = (node: VisualNode) => {
            const maxDegree = Math.max(...nodes.map((n) => n.degree), 1);
            const baseSize =
                minNodeSize +
                (maxNodeSize - minNodeSize) * (node.degree / maxDegree);
            return node.isHighestDegree ? baseSize * 1.5 : baseSize;
        };

        // Create force simulation
        const simulation = d3
            .forceSimulation<VisualNode>(nodes)
            .force(
                "link",
                d3
                    .forceLink<VisualNode, VisualEdge>(links)
                    .id((d) => d.id.toString())
                    .distance(30)
            )
            .force("charge", d3.forceManyBody().strength(-80))
            .force(
                "center",
                d3.forceCenter(width / 2, height / 2).strength(0.5)
            )
            .force(
                "collision",
                d3
                    .forceCollide<VisualNode>()
                    .radius((d) => getNodeSize(d) + 5)
                    .strength(0.8)
            )
            .alphaDecay(0.02);

        // Draw links
        const link = svg
            .append("g")
            .selectAll<SVGLineElement, VisualEdge>("line")
            .data(links)
            .enter()
            .append("line")
            .attr("stroke", (d) => (d.isHighDegree ? "#ff9500" : "#999"))
            .attr("stroke-opacity", (d) => (d.isHighDegree ? 0.8 : 0.6))
            .attr("stroke-width", (d) => (d.isHighDegree ? 2 : 1));

        // Draw nodes
        const node = svg
            .append("g")
            .selectAll<SVGCircleElement, VisualNode>("circle")
            .data(nodes)
            .enter()
            .append("circle")
            .attr("r", getNodeSize)
            .attr("fill", (d) => {
                if (d.isHighestDegree) return "#ff9500";
                return d.atCapacity ? "#ff4444" : "#1f77b4";
            })
            .attr("stroke", (d) => (d.isHighestDegree ? "#ff5500" : "#fff"))
            .attr("stroke-width", (d) => (d.isHighestDegree ? 3 : 1.5));

        // Add titles separately to avoid undefined errors
        node.append("title").text((d) => {
            if (!d) return "";
            return `Node ${d.id}\nDegree: ${d.degree}\nCapacity: ${d.capacity}${
                d.isHighestDegree ? "\n(Highest Degree Node)" : ""
            }`;
        });

        // Add node labels for high degree nodes with null checks
        const labelNodes = nodes.filter(
            (d) => d && (d.isHighestDegree || d.degree > nodes.length * 0.1)
        );

        svg.append("g")
            .selectAll<SVGTextElement, VisualNode>("text")
            .data(labelNodes)
            .enter()
            .append("text")
            .attr("dx", 12)
            .attr("dy", 4)
            .text((d) =>
                d ? `${d.id}${d.isHighestDegree ? " (Hub)" : ""}` : ""
            )
            .attr("font-size", "10px")
            .attr("font-weight", (d) =>
                d?.isHighestDegree ? "bold" : "normal"
            );

        // Update positions on simulation tick with null checks
        simulation.on("tick", () => {
            link.attr("x1", (d) => (d.source as VisualNode)?.x || 0)
                .attr("y1", (d) => (d.source as VisualNode)?.y || 0)
                .attr("x2", (d) => (d.target as VisualNode)?.x || 0)
                .attr("y2", (d) => (d.target as VisualNode)?.y || 0);

            svg.selectAll<SVGCircleElement, VisualNode>("circle")
                .attr("cx", (d) => d?.x || 0)
                .attr("cy", (d) => d?.y || 0);

            svg.selectAll<SVGTextElement, VisualNode>("text")
                .attr("x", (d) => d?.x || 0)
                .attr("y", (d) => d?.y || 0);
        });

        return () => {
            simulation.stop();
            resizeObserver.disconnect();
        };
    }, [network, minNodeSize, maxNodeSize, highestDegreeNode, simulationStep]);

    // Draw degree distribution
    useEffect(() => {
        if (!network || !distributionRef.current) return;

        // Clear previous visualization
        d3.select(distributionRef.current).selectAll("*").remove();

        // Get container dimensions
        const container = distributionRef.current;
        const width = container.clientWidth;
        const height = container.clientHeight;
        const margin = { top: 20, right: 20, bottom: 40, left: 50 };

        // Create SVG
        const svg = d3
            .select(distributionRef.current)
            .append("svg")
            .attr("width", "100%")
            .attr("height", "100%")
            .attr("viewBox", `0 0 ${width} ${height}`)
            .attr("preserveAspectRatio", "xMidYMid meet");

        // Handle resize
        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const newWidth = entry.contentRect.width;
                const newHeight = entry.contentRect.height;
                svg.attr("viewBox", `0 0 ${newWidth} ${newHeight}`);

                // Update scales
                xScale.range([margin.left, newWidth - margin.right]);
                yScale.range([newHeight - margin.bottom, margin.top]);

                // Update axes
                svg.select<SVGGElement>(".x-axis")
                    .attr(
                        "transform",
                        `translate(0, ${newHeight - margin.bottom})`
                    )
                    .call(d3.axisBottom(xScale) as any);

                svg.select<SVGGElement>(".y-axis")
                    .attr("transform", `translate(${margin.left}, 0)`)
                    .call(d3.axisLeft(yScale) as any);

                // Update bars
                svg.selectAll<SVGRectElement, (typeof data)[0]>("rect")
                    .attr("x", (d) => xScale(d.degree) - 5)
                    .attr("y", (d) => yScale(d.probability))
                    .attr(
                        "height",
                        (d) => newHeight - margin.bottom - yScale(d.probability)
                    );

                // Update labels
                svg.select(".x-label")
                    .attr("x", newWidth / 2)
                    .attr("y", newHeight - 5);

                svg.select(".y-label").attr(
                    "transform",
                    `translate(${margin.left - 30}, ${
                        newHeight / 2
                    }) rotate(-90)`
                );

                // Update hub annotation if it exists
                if (highestDegreeNode) {
                    const highestDegreeData = data.find(
                        (d) => d.degree === highestDegreeNode.degree
                    );
                    if (highestDegreeData) {
                        svg.select(".hub-annotation")
                            .attr("x", xScale(highestDegreeData.degree))
                            .attr(
                                "y",
                                yScale(highestDegreeData.probability) - 5
                            );
                    }
                }
            }
        });

        resizeObserver.observe(container);

        // Calculate degree distribution manually
        const maxDegree = Math.max(...network.nodes.map((node) => node.degree));
        const distribution = new Array(maxDegree + 1).fill(0);

        network.nodes.forEach((node) => {
            distribution[node.degree]++;
        });

        // Convert to probability
        const probabilityDistribution = distribution.map(
            (count) => count / network.nodes.length
        );

        // Prepare data for visualization
        const data = Array.from({ length: maxDegree + 1 }, (_, i) => ({
            degree: i,
            probability: probabilityDistribution[i],
            isHighestDegree: highestDegreeNode
                ? i === highestDegreeNode.degree
                : false,
        })).filter((d) => d.probability > 0);

        // Fit power law: P(k) ~ k^(-γ)
        const logData = data.map((d) => ({
            logDegree: Math.log(d.degree || 1), // Avoid log(0)
            logProb: Math.log(d.probability),
        }));

        // Only fit power law if we have enough data points
        let gamma: number | null = null;
        let intercept: number | null = null;

        if (logData.length >= 3) {
            // Require at least 3 points for a meaningful fit
            // Linear regression to find γ
            const n = logData.length;
            const sumX = logData.reduce((sum, d) => sum + d.logDegree, 0);
            const sumY = logData.reduce((sum, d) => sum + d.logProb, 0);
            const sumXY = logData.reduce(
                (sum, d) => sum + d.logDegree * d.logProb,
                0
            );
            const sumXX = logData.reduce(
                (sum, d) => sum + d.logDegree * d.logDegree,
                0
            );

            gamma = -(n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
            intercept = (sumY + gamma * sumX) / n + 0.5;

            // Only show fit if gamma is reasonable (typically between 1 and 4 for real networks)
            if (gamma < 1 || gamma > 4 || !isFinite(gamma)) {
                gamma = null;
                intercept = null;
            }
        }

        // Create scales
        const xScale = d3
            .scaleLinear()
            .domain([0, maxDegree])
            .range([margin.left, width - margin.right]);

        const yScale = d3
            .scaleLinear()
            .domain([0, Math.max(...data.map((d) => d.probability), 0.01)])
            .range([height - margin.bottom, margin.top]);

        // Create axes
        const xAxis = d3.axisBottom(xScale);
        const yAxis = d3.axisLeft(yScale);

        svg.append("g")
            .attr("class", "x-axis")
            .attr("transform", `translate(0, ${height - margin.bottom})`)
            .call(xAxis);

        svg.append("g")
            .attr("class", "y-axis")
            .attr("transform", `translate(${margin.left}, 0)`)
            .call(yAxis);

        // Add axes labels
        svg.append("text")
            .attr("class", "x-label")
            .attr("text-anchor", "middle")
            .attr("x", width / 2)
            .attr("y", height - 5)
            .text("Degree");

        // Draw power law fit only if we have valid gamma and intercept
        if (gamma !== null && intercept !== null) {
            // Start at degree = 1 to avoid undefined/negative values
            const powerLawData = Array.from({ length: maxDegree }, (_, i) => {
                const degree = i + 1;
                const probability = Math.max(
                    0,
                    Math.exp(intercept!) * Math.pow(degree, -gamma!)
                );
                return { degree, probability };
            });

            console.log(powerLawData);

            // Create line generator for power law
            const line = d3
                .line<{ degree: number; probability: number }>()
                .x((d) => xScale(d.degree))
                .y((d) => yScale(d.probability));

            // Draw the power law line
            svg.append("path")
                .datum(powerLawData)
                .attr("class", "power-law-line")
                .attr("fill", "none")
                .attr("stroke", "#ff4444")
                .attr("stroke-width", 2)
                .attr("d", line);

            // Add power law equation
            svg.append("text")
                .attr("class", "power-law-equation")
                .attr("x", width - margin.right - 10)
                .attr("y", margin.top + 20)
                .attr("text-anchor", "end")
                .attr("font-size", "12px")
                .attr("fill", "#ff4444")
                .text(`P(k) ~ k^(-${gamma.toFixed(2)})`);
        }

        // Draw bars
        svg.selectAll("rect")
            .data(data)
            .enter()
            .append("rect")
            .attr("x", (d) => xScale(d.degree) - 5)
            .attr("y", (d) => yScale(d.probability))
            .attr("width", 10)
            .attr(
                "height",
                (d) => height - margin.bottom - yScale(d.probability)
            )
            .attr("fill", (d) => (d.isHighestDegree ? "#ff9500" : "#1f77b4"))
            .attr("stroke", (d) => (d.isHighestDegree ? "#ff5500" : "none"))
            .attr("stroke-width", (d) => (d.isHighestDegree ? 2 : 0))
            .append("title")
            .text(
                (d) =>
                    `Degree: ${d.degree}\nProbability: ${d.probability.toFixed(
                        4
                    )}${d.isHighestDegree ? "\n(Highest Degree)" : ""}`
            );

        // Annotate the highest degree node's bar
        if (highestDegreeNode) {
            const highestDegreeData = data.find(
                (d) => d.degree === highestDegreeNode.degree
            );
            if (highestDegreeData) {
                svg.append("text")
                    .attr("class", "hub-annotation")
                    .attr("x", xScale(highestDegreeData.degree))
                    .attr("y", yScale(highestDegreeData.probability) - 5)
                    .attr("text-anchor", "middle")
                    .attr("font-size", "10px")
                    .attr("font-weight", "bold")
                    .attr("fill", "#ff5500")
                    .text("Hub");
            }
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [network, highestDegreeNode, simulationStep]);

    // Draw scatter plot of degree vs capacity
    useEffect(() => {
        if (!network || !scatterRef.current) return;

        // Clear previous visualization
        d3.select(scatterRef.current).selectAll("*").remove();

        // Get container dimensions
        const container = scatterRef.current;
        const width = container.clientWidth;
        const height = container.clientHeight;
        const margin = { top: 20, right: 20, bottom: 40, left: 50 };

        // Create SVG
        const svg = d3
            .select(scatterRef.current)
            .append("svg")
            .attr("width", "100%")
            .attr("height", "100%")
            .attr("viewBox", `0 0 ${width} ${height}`)
            .attr("preserveAspectRatio", "xMidYMid meet");

        // Handle resize
        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const newWidth = entry.contentRect.width;
                const newHeight = entry.contentRect.height;
                svg.attr("viewBox", `0 0 ${newWidth} ${newHeight}`);

                // Update scales
                xScale.range([margin.left, newWidth - margin.right]);
                yScale.range([newHeight - margin.bottom, margin.top]);

                // Update axes
                svg.select<SVGGElement>(".x-axis")
                    .attr(
                        "transform",
                        `translate(0, ${newHeight - margin.bottom})`
                    )
                    .call(d3.axisBottom(xScale) as any);

                svg.select<SVGGElement>(".y-axis")
                    .attr("transform", `translate(${margin.left}, 0)`)
                    .call(d3.axisLeft(yScale) as any);

                // Update capacity line
                svg.select<SVGLineElement>(".capacity-line")
                    .attr("x2", xScale(maxCapacity))
                    .attr("y2", yScale(maxCapacity));

                // Update capacity line label
                svg.select<SVGTextElement>(".capacity-label")
                    .attr("x", xScale(maxCapacity * 0.7))
                    .attr("y", yScale(maxCapacity * 0.7) - 10);

                // Update scatter points
                svg.selectAll<SVGCircleElement, (typeof data)[0]>("circle")
                    .attr("cx", (d) => xScale(d.capacity))
                    .attr("cy", (d) => yScale(d.degree));

                // Update hub label if it exists
                if (highestDegreeNode) {
                    const highestDegreeData = data.find(
                        (d) => d.id === highestDegreeNode.id
                    );
                    if (highestDegreeData) {
                        svg.select(".hub-label")
                            .attr("x", xScale(highestDegreeData.capacity))
                            .attr("y", yScale(highestDegreeData.degree) - 10);
                    }
                }

                // Update labels
                svg.select(".x-label")
                    .attr("x", newWidth / 2)
                    .attr("y", newHeight - 5);

                svg.select(".y-label").attr(
                    "transform",
                    `translate(${margin.left - 30}, ${
                        newHeight / 2
                    }) rotate(-90)`
                );
            }
        });

        resizeObserver.observe(container);

        // Prepare scatter plot data
        const data = network.nodes.map((node) => ({
            id: node.id,
            degree: node.degree,
            capacity: node.capacity,
            atCapacity: node.degree >= node.capacity,
            isHighestDegree: highestDegreeNode
                ? node.id === highestDegreeNode.id
                : false,
        }));

        // Create scales
        const xScale = d3
            .scaleLinear()
            .domain([0, Math.max(...data.map((d) => d.capacity))])
            .range([margin.left, width - margin.right]);

        const yScale = d3
            .scaleLinear()
            .domain([0, Math.max(...data.map((d) => d.degree)) * 1.1])
            .range([height - margin.bottom, margin.top]);

        // Create axes
        const xAxis = d3.axisBottom(xScale);
        const yAxis = d3.axisLeft(yScale);

        svg.append("g")
            .attr("class", "x-axis")
            .attr("transform", `translate(0, ${height - margin.bottom})`)
            .call(xAxis);

        svg.append("g")
            .attr("class", "y-axis")
            .attr("transform", `translate(${margin.left}, 0)`)
            .call(yAxis);

        // Add axes labels
        svg.append("text")
            .attr("class", "x-label")
            .attr("text-anchor", "middle")
            .attr("x", width / 2)
            .attr("y", height - 5)
            .text("Capacity");

        svg.append("text")
            .attr("class", "y-label")
            .attr("text-anchor", "middle")
            .attr(
                "transform",
                `translate(${margin.left - 30}, ${height / 2}) rotate(-90)`
            )
            .text("Degree");

        // Draw capacity line (y = x)
        const maxCapacity = Math.max(...data.map((d) => d.capacity));
        svg.append("line")
            .attr("class", "capacity-line")
            .attr("x1", xScale(0))
            .attr("y1", yScale(0))
            .attr("x2", xScale(maxCapacity))
            .attr("y2", yScale(maxCapacity))
            .attr("stroke", "#ff4444")
            .attr("stroke-dasharray", "5,5")
            .attr("stroke-width", 1);

        // Add annotation for capacity line
        svg.append("text")
            .attr("class", "capacity-label")
            .attr("x", xScale(maxCapacity * 0.7))
            .attr("y", yScale(maxCapacity * 0.7) - 10)
            .attr("text-anchor", "end")
            .attr("font-size", "10px")
            .attr("fill", "#ff4444")
            .text("Capacity Limit");

        // Draw scatter plot points
        svg.selectAll("circle")
            .data(data)
            .enter()
            .append("circle")
            .attr("cx", (d) => xScale(d.capacity))
            .attr("cy", (d) => yScale(d.degree))
            .attr("r", (d) => (d.isHighestDegree ? 8 : 5))
            .attr("fill", (d) => {
                if (d.isHighestDegree) return "#ff9500";
                return d.atCapacity ? "#ff4444" : "#1f77b4";
            })
            .attr("stroke", (d) => (d.isHighestDegree ? "#ff5500" : "none"))
            .attr("stroke-width", (d) => (d.isHighestDegree ? 2 : 0))
            .append("title")
            .text((d) => {
                return `Node ${d.id}\nDegree: ${d.degree}\nCapacity: ${
                    d.capacity
                }${d.isHighestDegree ? "\n(Highest Degree Node)" : ""}`;
            });

        // Label the highest degree node
        if (highestDegreeNode) {
            const highestDegreeData = data.find(
                (d) => d.id === highestDegreeNode.id
            );
            if (highestDegreeData) {
                svg.append("text")
                    .attr("class", "hub-label")
                    .attr("x", xScale(highestDegreeData.capacity))
                    .attr("y", yScale(highestDegreeData.degree) - 10)
                    .attr("text-anchor", "middle")
                    .attr("font-size", "10px")
                    .attr("font-weight", "bold")
                    .attr("fill", "#ff5500")
                    .text(`Node ${highestDegreeData.id} (Hub)`);
            }
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [network, highestDegreeNode, simulationStep]);

    return (
        <div className="w-full">
            <div className="bg-white p-4 rounded shadow mb-4">
                <div className="flex justify-between items-end">
                    <div className="flex-1">
                        <h2 className="text-lg font-bold mb-2">Model Setup</h2>
                        <div className="grid grid-cols-4 gap-2">
                            <div className="relative group">
                                <label className="block text-sm font-medium text-gray-700">
                                    Initial Nodes
                                    <input
                                        type="number"
                                        min="2"
                                        max="20"
                                        value={initialNodes}
                                        onChange={(e) =>
                                            setInitialNodes(
                                                parseInt(e.target.value)
                                            )
                                        }
                                        disabled={isInitialized}
                                        className={`w-full p-1 border rounded mt-1 text-sm ${
                                            isInitialized ? "bg-gray-100" : ""
                                        }`}
                                    />
                                </label>
                                <div className="absolute left-0 hidden group-hover:block bg-gray-800 text-white text-xs rounded p-2 w-48 z-10">
                                    Number of nodes to start the network with
                                </div>
                            </div>

                            <div className="relative group">
                                <label className="block text-sm font-medium text-gray-700">
                                    Min Capacity
                                    <input
                                        type="number"
                                        min="1"
                                        max={maxCapacity}
                                        value={minCapacity}
                                        onChange={(e) =>
                                            setMinCapacity(
                                                parseInt(e.target.value)
                                            )
                                        }
                                        disabled={isInitialized}
                                        className={`w-full p-1 border rounded mt-1 text-sm ${
                                            isInitialized ? "bg-gray-100" : ""
                                        }`}
                                    />
                                </label>
                                <div className="absolute left-0 hidden group-hover:block bg-gray-800 text-white text-xs rounded p-2 w-48 z-10">
                                    Minimum number of connections a node can
                                    handle
                                </div>
                            </div>

                            <div className="relative group">
                                <label className="block text-sm font-medium text-gray-700">
                                    Max Capacity
                                    <input
                                        type="number"
                                        min={minCapacity}
                                        max="50"
                                        value={maxCapacity}
                                        onChange={(e) =>
                                            setMaxCapacity(
                                                parseInt(e.target.value)
                                            )
                                        }
                                        disabled={isInitialized}
                                        className={`w-full p-1 border rounded mt-1 text-sm ${
                                            isInitialized ? "bg-gray-100" : ""
                                        }`}
                                    />
                                </label>
                                <div className="absolute left-0 hidden group-hover:block bg-gray-800 text-white text-xs rounded p-2 w-48 z-10">
                                    Maximum number of connections a node can
                                    handle
                                </div>
                            </div>

                            <div className="relative group">
                                <label className="block text-sm font-medium text-gray-700">
                                    Edges Per Node (m)
                                    <input
                                        type="number"
                                        min="1"
                                        max="10"
                                        value={edgesPerNode}
                                        onChange={(e) =>
                                            setEdgesPerNode(
                                                parseInt(e.target.value)
                                            )
                                        }
                                        disabled={isInitialized}
                                        className={`w-full p-1 border rounded mt-1 text-sm ${
                                            isInitialized ? "bg-gray-100" : ""
                                        }`}
                                    />
                                </label>
                                <div className="absolute left-0 hidden group-hover:block bg-gray-800 text-white text-xs rounded p-2 w-48 z-10">
                                    Number of edges to add for each new node
                                </div>
                            </div>
                        </div>
                    </div>

                    <button
                        className={`${
                            isInitialized
                                ? "bg-red-500 hover:bg-red-600"
                                : "bg-blue-500 hover:bg-blue-600"
                        } text-white font-bold py-3 px-6 rounded text-lg ml-4 w-32`}
                        onClick={initializeSimulation}
                        disabled={simulating}
                    >
                        {simulating
                            ? "Initializing..."
                            : isInitialized
                            ? "Clear"
                            : "Initialize"}
                    </button>
                </div>
            </div>

            {isInitialized && (
                <>
                    <div className="bg-white p-4 rounded shadow mb-4">
                        <h2 className="text-lg font-bold mb-2">
                            Simulation Controls
                        </h2>
                        <div className="flex flex-col space-y-4">
                            <div className="flex items-center space-x-4">
                                <button
                                    className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded w-24"
                                    onClick={stepSimulation}
                                    disabled={!network}
                                >
                                    Step
                                </button>

                                <button
                                    className={`${
                                        isPlaying
                                            ? "bg-orange-500 hover:bg-orange-600"
                                            : "bg-green-500 hover:bg-green-600"
                                    } text-white font-bold py-2 px-4 rounded w-24`}
                                    onClick={() => setIsPlaying(!isPlaying)}
                                    disabled={!network}
                                >
                                    {isPlaying ? "Pause" : "Play"}
                                </button>

                                <div
                                    className="inline-flex rounded-md shadow-sm"
                                    role="group"
                                >
                                    <button
                                        className={`px-4 py-2 text-sm font-medium border ${
                                            stepDelay === 50
                                                ? "bg-blue-500 text-white border-blue-500"
                                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                        } rounded-l-lg w-24`}
                                        onClick={() => setStepDelay(50)}
                                    >
                                        Fast
                                    </button>
                                    <button
                                        className={`px-4 py-2 text-sm font-medium border-t border-b ${
                                            stepDelay === 250
                                                ? "bg-blue-500 text-white border-blue-500"
                                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                        } w-24`}
                                        onClick={() => setStepDelay(250)}
                                    >
                                        Medium
                                    </button>
                                    <button
                                        className={`px-4 py-2 text-sm font-medium border ${
                                            stepDelay === 500
                                                ? "bg-blue-500 text-white border-blue-500"
                                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                        } rounded-r-lg w-24`}
                                        onClick={() => setStepDelay(500)}
                                    >
                                        Slow
                                    </button>
                                </div>
                            </div>

                            <div className="flex flex-col space-y-2">
                                <div className="flex justify-between items-center">
                                    <h3 className="text-sm font-bold text-gray-700">
                                        History
                                    </h3>
                                    <div className="flex items-center space-x-2">
                                        {sheddingEvents[
                                            currentHistoryIndex
                                        ] && (
                                            <span className="text-xs text-red-500 font-medium">
                                                Shedding Event
                                            </span>
                                        )}
                                        <span className="text-sm text-gray-600 w-32 text-right">
                                            Step {currentHistoryIndex + 1} of{" "}
                                            {networkHistory.length}
                                        </span>
                                    </div>
                                </div>
                                <div className="relative">
                                    <input
                                        type="range"
                                        min="0"
                                        max={networkHistory.length - 1}
                                        value={currentHistoryIndex}
                                        onChange={(e) =>
                                            handleHistoryChange(
                                                parseInt(e.target.value)
                                            )
                                        }
                                        className="w-full"
                                    />
                                    <div className="absolute top-0 left-0 right-0 h-1 pointer-events-none">
                                        {sheddingEvents.map(
                                            (didShed, index) => (
                                                <div
                                                    key={index}
                                                    className={`absolute h-1 w-1 rounded-full ${
                                                        didShed
                                                            ? "bg-red-500"
                                                            : "bg-transparent"
                                                    }`}
                                                    style={{
                                                        left: `${
                                                            (index /
                                                                (networkHistory.length -
                                                                    1)) *
                                                            100
                                                        }%`,
                                                        transform:
                                                            "translateX(-50%)",
                                                    }}
                                                />
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {stats && (
                        <div className="bg-white p-4 rounded shadow mb-4">
                            <h2 className="text-lg font-bold mb-2">
                                Network Statistics
                            </h2>

                            <div className="grid grid-cols-3 gap-8">
                                <div className="space-y-1">
                                    <p className="text-base flex items-center">
                                        <span className="text-gray-600 w-32 font-semibold">
                                            Step
                                        </span>
                                        <span>{currentHistoryIndex + 1}</span>
                                    </p>
                                    <p className="text-base flex items-center">
                                        <span className="text-gray-600 w-32 font-semibold">
                                            Nodes
                                        </span>
                                        <span>{stats.nodeCount}</span>
                                    </p>
                                    <p className="text-base flex items-center">
                                        <span className="text-gray-600 w-32 font-semibold">
                                            Edges
                                        </span>
                                        <span>{stats.edgeCount}</span>
                                    </p>
                                </div>

                                <div className="space-y-1">
                                    <p className="text-base flex items-center">
                                        <span className="text-gray-600 w-32 font-semibold">
                                            Avg Degree
                                        </span>
                                        <span>
                                            {stats.averageDegree.toFixed(1)}
                                        </span>
                                    </p>
                                    <p className="text-base flex items-center">
                                        <span className="text-gray-600 w-32 font-semibold">
                                            Avg Capacity
                                        </span>
                                        <span>
                                            {stats.averageCapacity.toFixed(1)}
                                        </span>
                                    </p>
                                    <p className="text-base flex items-center">
                                        <span className="text-gray-600 w-32 font-semibold">
                                            At Capacity
                                        </span>
                                        <span>
                                            {stats.percentAtCapacity.toFixed(1)}
                                            %
                                        </span>
                                    </p>
                                </div>

                                <div className="space-y-1">
                                    {highestDegreeNode && (
                                        <p className="text-base flex items-center">
                                            <span className="text-gray-600 w-32 font-semibold">
                                                Hub Node
                                            </span>
                                            <span>{highestDegreeNode.id}</span>
                                        </p>
                                    )}
                                    {highestDegreeNode && (
                                        <p className="text-base flex items-center">
                                            <span className="text-gray-600 w-32 font-semibold">
                                                Hub Degree
                                            </span>
                                            <span>
                                                {highestDegreeNode.degree}/
                                                {highestDegreeNode.capacity}
                                            </span>
                                        </p>
                                    )}
                                    <p className="text-base flex items-center">
                                        <span className="text-gray-600 w-32 font-semibold">
                                            Avg Steps/Shed
                                        </span>
                                        <span>
                                            {sheddingEvents.filter(Boolean)
                                                .length > 0
                                                ? (
                                                      simulationStep /
                                                      sheddingEvents.filter(
                                                          Boolean
                                                      ).length
                                                  ).toFixed(1)
                                                : "—"}
                                        </span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 gap-4">
                        <div className="bg-white p-4 rounded shadow">
                            <h2 className="text-lg font-bold mb-2">
                                Network Visualization
                            </h2>
                            <div
                                ref={networkRef}
                                className="border rounded p-2 h-[600px]"
                            ></div>

                            <div className="mt-4 text-sm">
                                <p>
                                    <strong>Legend:</strong>
                                </p>
                                <div className="flex items-center mt-1">
                                    <div className="w-4 h-4 bg-blue-500 rounded-full mr-2"></div>
                                    <span>Normal Node</span>
                                </div>
                                <div className="flex items-center mt-1">
                                    <div className="w-4 h-4 bg-red-500 rounded-full mr-2"></div>
                                    <span>Node At Capacity</span>
                                </div>
                                <div className="flex items-center mt-1">
                                    <div className="w-4 h-4 bg-orange-500 rounded-full mr-2"></div>
                                    <span>Highest Degree Node (Hub)</span>
                                </div>
                                <div className="flex items-center mt-1">
                                    <div className="w-4 h-1 bg-orange-500 mr-2"></div>
                                    <span>Connections to Hub</span>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-white p-4 rounded shadow">
                                <h2 className="text-lg font-bold mb-2">
                                    Degree Distribution
                                </h2>
                                <div
                                    ref={distributionRef}
                                    className="border rounded p-2 h-[400px]"
                                ></div>
                            </div>

                            <div className="bg-white p-4 rounded shadow">
                                <h2 className="text-lg font-bold mb-2">
                                    Degree vs. Capacity
                                </h2>
                                <div
                                    ref={scatterRef}
                                    className="border rounded p-2 h-[400px]"
                                ></div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default NetworkVisualizer;
